import React, { useState } from 'react'
import cn from 'classnames'
import PropTypes from 'prop-types'
import Cookie from '!!@svgr/webpack!./cookie.svg'
import { setItem, getItem } from 'js/storeManager'
import { isSSR } from 'js/isSSR'
import { FormattedMessage } from 'react-intl'
import { clearAllCookies, GetPolicyLink } from '../../utils'
import CloseIcon from '!!@svgr/webpack!media/close.svg'
import Overlay, { OverlayWrapper } from '../Overlay/Overlay'

const Button = ({ children, className, secondary, ...rest }) => (
  <button
    className={cn(
      'block pt-8 pb-6 px-24',
      'type-small-fat leading-default',
      'rounded-full transition',
      secondary
        ? 'text-BrandDark border-2 border-Brand hover:border-BrandDark'
        : 'text-TypeConstant bg-Brand hover:bg-BrandDark shadow-elevated',
      className
    )}
    {...rest}
  >
    {children}
  </button>
)

Button.propTypes = {
  children: PropTypes.any,
  className: PropTypes.string,
  secondary: PropTypes.bool,
}

const Info = ({ onDone, onSettings }) => (
  <>
    <h2 className="type-upper-fat mb-8">
      <FormattedMessage id="cookies_heading" defaultMessage="Cookies" />
    </h2>
    <p className="type-small leading-body mr-8 md:mr-auto mb-32">
      <FormattedMessage
        id="cookies_text_privacy_policy"
        defaultMessage="We use cookies to recognize your preference and improve the website experience by collecting information about bugs, analyzing usage data and customize ads. If you allow all cookies, it may mean that some data will be shared with third parties, such as our marketing partners. You can adjust your cookie preferences at any time at the bottom of this website. For more information on how we use cookies, please see our <a>Privacy Policy</a>."
        values={{
          a: function Link(text) {
            return (
              <a
                className={`
                transition text-blue-dark hover:text-Type
                border-b-1 border-blue-dark border-opacity-25
                hover:border-opacity-100 hover:border-Type
              `}
                href={`${GetPolicyLink()}#cookies`}
              >
                {text}
              </a>
            )
          },
        }}
      />
    </p>
    <div className="grid grid-cols-2 gap-2">
      <Button onClick={() => onDone(['analytics', 'marketing'])}>
        <FormattedMessage
          id="cookies_allow_all"
          defaultMessage="Allow all cookies"
        />
      </Button>
      <Button
        onClick={() => {
          onDone([], false)
        }}
      >
        <FormattedMessage
          id="cookies_necessary"
          defaultMessage="Use necessary cookies only"
        />
      </Button>
    </div>
    <button
      className={`
        mt-20 transition
        type-small-fat text-BrandDark
        border-b-2 border-BrandDark
        hover:text-Type hover:border-Type
      `}
      onClick={onSettings}
    >
      <FormattedMessage id="cookies_options" defaultMessage="More options" />
    </button>
  </>
)

Info.propTypes = {
  onDone: PropTypes.func,
  onSettings: PropTypes.func,
}

const Checkbox = ({ type, onChange, acceptedCookies = [] }) => {
  let title, text

  switch (type) {
    case 'required':
      title = (
        <FormattedMessage
          id="cookies_required_title"
          defaultMessage="Strictly necessary cookies"
        />
      )
      text = (
        <FormattedMessage
          id="cookies_required_text"
          defaultMessage="These cookies are required for optimal website functionality."
        />
      )
      break
    case 'analytics':
      title = (
        <FormattedMessage
          id="cookies_analytics_title"
          defaultMessage="Performance and analytics cookies"
        />
      )
      text = (
        <FormattedMessage
          id="cookies_analytics_text"
          defaultMessage="Allow us to measure usage in order to analyze and improve the site."
        />
      )
      break
    case 'marketing':
      title = (
        <FormattedMessage
          id="cookies_marketing_title"
          defaultMessage="Marketing cookies"
        />
      )
      text = (
        <FormattedMessage
          id="cookies_marketing_text"
          defaultMessage="Measures the performance of campaigns and customize ads."
        />
      )
      break
    default:
      break
  }

  return (
    <div className="c-check mb-24">
      <input
        className="c-check__input"
        type="checkbox"
        id={`${type}-cookies`}
        onChange={onChange}
        defaultChecked={acceptedCookies.includes(type) || type === 'required'}
        disabled={type === 'required'}
      />
      <label className="c-check__label" htmlFor={`${type}-cookies`}>
        <span className="font-demibold">{title}</span>
        <span className="block mt-4 text-TypeSub">{text}</span>
      </label>
    </div>
  )
}

Checkbox.propTypes = {
  type: PropTypes.oneOf(['required', 'analytics', 'marketing']).isRequired,
  onChange: PropTypes.func,
  acceptedCookies: PropTypes.array,
}

const Settings = ({ onDone, shouldLoadSettings }) => {
  const [acceptedCookies, setAcceptedCookies] = useState(
    JSON.parse(getItem('accepted-cookies')) || []
  )

  const handleChange = (type) => {
    if (acceptedCookies.includes(type)) {
      setAcceptedCookies(acceptedCookies.filter((cookie) => cookie !== type))
    } else {
      setAcceptedCookies([...acceptedCookies, type])
    }
  }

  const handleAllowSelected = () => {
    if (shouldLoadSettings) {
      const state = getItem('state')
      clearAllCookies()
      sessionStorage.clear()
      localStorage.clear()
      setItem('accepted-cookies', JSON.stringify(acceptedCookies))
      if (state) setItem('state', state)
      location.reload()
    } else {
      onDone(acceptedCookies)
    }
  }

  return (
    <form className="type-small text-left">
      <Checkbox type="required" />
      <Checkbox
        type="analytics"
        onChange={() => handleChange('analytics')}
        acceptedCookies={acceptedCookies}
      />
      <Checkbox
        type="marketing"
        onChange={() => handleChange('marketing')}
        acceptedCookies={acceptedCookies}
      />
      <div className="grid grid-cols-2 gap-2 mt-32">
        <Button onClick={handleAllowSelected} type="button">
          <FormattedMessage
            id="cookies_allow_selected"
            defaultMessage="Allow selected"
          />
        </Button>
        <Button
          secondary
          type="button"
          onClick={() => onDone(['analytics', 'marketing'])}
        >
          <FormattedMessage
            id="cookies_allow_all"
            defaultMessage="Allow all cookies"
          />
        </Button>
      </div>
    </form>
  )
}

Settings.propTypes = {
  onDone: PropTypes.func,
  shouldLoadSettings: PropTypes.bool,
}

const CookieConsent = ({
  forceServerSideRendering = false,
  shouldLoadSettings = false,
  onClose,
}) => {
  const [hasMadeAChoice, setHasMadeAChoice] = useState(
    getItem('accepted-cookies') && !shouldLoadSettings
  )
  const [showSettings, setShowSettings] = useState(shouldLoadSettings)

  if (isSSR && !forceServerSideRendering) return null
  if (!isSSR && navigator.userAgent.match('DisableForTesting')) {
    return null
  }
  if (hasMadeAChoice) return null

  const onDone = (acceptedCookies, loadTracking = true) => {
    setHasMadeAChoice(true)
    setItem('accepted-cookies', JSON.stringify(acceptedCookies))
    if (loadTracking) document.dispatchEvent(new CustomEvent('loadTracking'))
  }

  let OverlayComponent = Overlay
  if (forceServerSideRendering) {
    OverlayComponent = OverlayWrapper
  }

  return (
    <OverlayComponent>
      <div
        className={`
        absolute bottom-0 left-0 right-0 md:right-auto
        md:w-1/2 md:max-w-480
        md:mb-24 md:mx-24
        pt-56 pb-24 px-20
        bg-white shadow-elevated md:rounded-8
        md:border-1 border-Border
        text-14 lg:text-17 text-center
      `}
      >
        {shouldLoadSettings && onClose && (
          <button
            type="button"
            className="absolute right-0 top-0 mt-20 mr-20 text-grey-light"
            onClick={onClose}
          >
            <span className="sr-only">Close</span>
            <CloseIcon />
          </button>
        )}
        <div className="pt-6 absolute top-0 left-1/2">
          <Cookie className="transform -translate-x-1/2 -translate-y-1/2" />
        </div>
        {showSettings ? (
          <Settings onDone={onDone} shouldLoadSettings={shouldLoadSettings} />
        ) : (
          <Info onDone={onDone} onSettings={() => setShowSettings(true)} />
        )}
      </div>
    </OverlayComponent>
  )
}

CookieConsent.propTypes = {
  shouldLoadSettings: PropTypes.bool,
  onClose: PropTypes.func,
  forceServerSideRendering: PropTypes.bool,
}

export default CookieConsent
